/** @jsxImportSource @emotion/react */
import { zodResolver } from '@hookform/resolvers/zod'
import { Box, Button, useTheme } from '@mui/material'
import {
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@app/components/ui/Dialog/Dialog'
import { toast } from '@app/components/ui/Toast/toast'
import { appLink } from '@app/config/appLink'
import { fetchRiskWorkspaceById } from '@app/services/riskService'
import { riskAvailableWorkspacesAtom, riskUpdateWorkspaceAtom } from '@app/state/riskAtoms'
import { useQuery } from '@tanstack/react-query'
import { useAtom, useAtomValue } from 'jotai'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import {
  WorkspaceFormData,
  WorkspaceSchema,
  workspaceToModalLayers,
} from './utils/Workspaces.utils'
import { contentAreaStyles } from './WorkspacesDialog.styles'
import WorkspacesDialogLoading from './WorkspacesDialogLoading'
import WorkspacesForm from './WorkspacesForm'
import { WorkspaceNavbar } from './WorkspaceNavbar/WorkspaceNavbar'
import { ModalLayer } from '@app/components/Molecules/LayersControl'
import { useSelector } from 'react-redux'
import { RootState } from '@app/redux/store'
import { AxiosError } from 'axios'
import { useMapLayers } from '@app/hooks/useMapLayers'
import { queryClient } from '@app/config/queryClient'
import { useMap } from '@app/contexts/ModuleMapContexts/RiskMapContext/RiskMapContext'
import { DrawArea } from '@app/redux/riskMap/riskMapSlice'

// local config, DRY, easier to edit
const config = {
  heading: 'Edit Workspace',
  description: undefined,
}

export const WorkspaceEdit = () => {
  const theme = useTheme()
  const navigate = useNavigate()
  const { workspaceId } = useParams()
  const { data: workspaces } = useAtomValue(riskAvailableWorkspacesAtom)
  const [{ mutate: updateRiskWorkspace }] = useAtom(riskUpdateWorkspaceAtom)
  const { layerData, drawAreas: mapDrawAreas } = useSelector((state: RootState) => state.riskMap)
  const { regionMasks } = useMap()
  const [layers, setLayers] = useState<ModalLayer[] | null>(null)
  const [shouldUpdateLayers, setShouldUpdateLayers] = useState(false)
  // Working copy of the filters that will be saved. Initialised from the saved
  // workspace; overwritten by "Update from Map" with the live map filters.
  const [regions, setRegions] = useState<string[]>([])
  const [drawAreas, setDrawAreas] = useState<DrawArea[]>([])
  const {
    layers: currentMapLayers,
    elementLayers,
    hazardLayers,
    informationLayers,
  } = useMapLayers()

  const { data, isLoading, error } = useQuery({
    queryKey: ['workspace', workspaceId],
    queryFn: () => fetchRiskWorkspaceById(workspaceId as string),
    enabled: !!workspaceId,
  })

  const form = useForm<WorkspaceFormData>({
    resolver: zodResolver(WorkspaceSchema),
    defaultValues: {
      name: '',
      description: '',
      visibility: 'private',
    },
  })

  // update form when data loads
  useEffect(() => {
    if (data && layerData) {
      form.reset({
        name: data.name,
        description: data.description || '',
        visibility: data.visibility || 'private',
      })
      setLayers(workspaceToModalLayers(data, layerData))
      setShouldUpdateLayers(false)
      setRegions(data.regions ?? [])
      setDrawAreas(data.drawAreas ?? [])
    }
  }, [data, form, layerData])

  const onEditWorkspaceSubmit = async (formData: WorkspaceFormData) => {
    if (!workspaceId || !workspaces || !data) return

    const duplicate = workspaces.some(
      (state) =>
        state.id !== workspaceId &&
        state.name.trim().toLowerCase() === formData.name.trim().toLowerCase(),
    )

    if (duplicate) {
      form.setError('name', { message: 'A save state with this name already exists' })
      return
    }

    const updatedWorkspace = {
      name: formData.name.trim(),
      description: formData.description?.trim() || '',
      visibility: formData.visibility,
      layers: shouldUpdateLayers
        ? {
            assets: elementLayers,
            scenarios: hazardLayers,
            contexts: informationLayers,
          }
        : data.layers,
      regions: regions.length ? regions : null,
      drawAreas,
    }

    updateRiskWorkspace(
      {
        id: workspaceId,
        updatedWorkspace,
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ['workspace', workspaceId] })
          navigate(appLink.riskWorkspaces.path)
          toast({
            title: (
              <span>
                <b>{formData.name}</b> has been updated
              </span>
            ),
            duration: 3000,
            type: 'success',
            position: 'top-right',
          })
        },
        onError: (error: unknown) => {
          const errorMessage =
            error instanceof AxiosError && error.response?.data?.error
              ? `Error: ${error.response?.data?.error}`
              : 'An error occurred while updating the Workspace'
          form.setError('root', {
            message: errorMessage,
          })
        },
      },
    )
  }

  const onCancel = () => {
    navigate(appLink.riskWorkspaces.path)
  }

  const handleUpdateFromMap = () => {
    setLayers(currentMapLayers)
    setShouldUpdateLayers(true)
    setRegions(regionMasks?.map((r) => r.region) ?? [])
    setDrawAreas(mapDrawAreas)
  }

  if (isLoading)
    return <WorkspacesDialogLoading heading={config.heading} description={config.description} />

  if (error)
    return (
      <WorkspacesDialogLoading heading={config.heading} description={config.description} isError />
    )

  return (
    <Box css={contentAreaStyles(theme)}>
      <WorkspaceNavbar currentTab="saved" />
      <DialogHeader>
        <DialogTitle>{config.heading}</DialogTitle>
        <DialogDescription>{config.description}</DialogDescription>
      </DialogHeader>

      <WorkspacesForm form={form} layers={layers || []} regions={regions} drawAreas={drawAreas} />

      <DialogFooter>
        <Box sx={{ display: 'flex', gap: 1, flexGrow: 1 }}>
          <Button variant="outlined" onClick={handleUpdateFromMap}>
            Update from Map
          </Button>
        </Box>
        <Button variant="outlined" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={form.handleSubmit(onEditWorkspaceSubmit)}
          disabled={form.formState.isSubmitting}
        >
          {form.formState.isSubmitting ? 'Updating...' : 'Update'}
        </Button>
      </DialogFooter>
    </Box>
  )
}
